// ATE-CR-117
import { NavLink, Link } from "react-router-dom";
import {
  ChevronLeft,
  ChevronRight,
  FileText,
  Info,
  Mail,
} from "lucide-react";

import { useTenantContext } from "@/context/TenantContext";
import { TenantSwitcher } from "@/components/navigation/TenantSwitcher";
import { NAV_HQ, NAV_TENANT } from "@/constants/nav";

import "./HubSidebar.css";

export interface HubSidebarProps {
  readonly collapsed: boolean;
  readonly onToggleCollapse: () => void;
  /** Mobile only: sidebar opens as an overlay drawer */
  readonly mobileOpen?: boolean;
  readonly onNavClick?: () => void;
  /** DOM id forwarded to the <aside> so HubTopBar.aria-controls can target it. */
  readonly id?: string;
}

const FOOTER_LINKS = [
  { to: "mailto:contact@soft9tech.com", label: "Contact", icon: Mail },
  { to: "https://soft9tech.com/about", label: "About", icon: Info },
  { to: "https://soft9tech.com/terms", label: "Terms", icon: FileText },
] as const;

// ATE-CR-117: Scope-aware HubSidebar — reads useTenantContext() and renders
// NAV_HQ or NAV_TENANT accordingly. TenantSwitcher is mounted between the
// logo header and the nav menu.
//
// Collapsed-mode decision: When `collapsed=true` the TenantSwitcher is hidden
// via `display: none` (CSS class `hub-sidebar__scope-switcher--hidden`).
// Rationale: the combobox trigger is 180px+ wide and cannot be meaningfully
// compressed to icon-only without a separate design pass. The collapsed sidebar
// already hides all nav labels — hiding the switcher is consistent. If the
// operator needs to switch scope while collapsed, they can expand the sidebar
// first. This is noted in changelog §Implementation Task 4.
export function HubSidebar({
  collapsed,
  onToggleCollapse,
  mobileOpen = false,
  onNavClick,
  id,
}: HubSidebarProps): JSX.Element {
  const { scope } = useTenantContext();
  const navItems = scope.mode === "hq" ? NAV_HQ : NAV_TENANT;

  return (
    <aside
      id={id}
      className={[
        "hub-sidebar",
        collapsed ? "hub-sidebar--collapsed" : "",
        mobileOpen ? "hub-sidebar--mobile-open" : "",
      ]
        .filter(Boolean)
        .join(" ")}
      aria-label="Hub navigation"
    >
      {/* Logo */}
      <div className="hub-sidebar__header">
        {!collapsed ? (
          <Link
            to="/"
            className="hub-sidebar__logo"
            aria-label="Soft9Tech Admin — Home"
          >
            <img
              src="/images/logo.png"
              alt="Soft9Tech Admin"
              className="hub-sidebar__logo-img"
            />
          </Link>
        ) : (
          <Link
            to="/"
            className="hub-sidebar__logo-icon"
            aria-label="Soft9Tech Admin — Home"
          >
            <img
              src="/images/soft9tech-48-X-48.png"
              alt="Soft9Tech Admin"
              className="hub-sidebar__logo-img hub-sidebar__logo-img--icon"
            />
          </Link>
        )}
      </div>

      {/* Scope switcher — hidden when collapsed (see collapsed-mode decision above) */}
      <div
        data-testid="hub-sidebar-scope-switcher"
        aria-hidden={collapsed}
        className={[
          "hub-sidebar__scope-switcher",
          collapsed ? "hub-sidebar__scope-switcher--hidden" : "",
        ]
          .filter(Boolean)
          .join(" ")}
      >
        <TenantSwitcher />
      </div>

      {/* Navigation */}
      <nav
        className="hub-sidebar__nav"
        role="navigation"
        aria-label="Hub sections"
      >
        <ul className="hub-sidebar__nav-list">
          {navItems.map(({ to, label, icon: Icon, end }) => (
            <li key={to} className="hub-sidebar__nav-item">
              <NavLink
                to={to}
                end={end}
                className={({ isActive }): string =>
                  `hub-sidebar__nav-link${isActive ? " hub-sidebar__nav-link--active" : ""}`
                }
                // Collapsed mode hides the visible label, so surface it as an
                // accessible name (title alone is an insufficient a11y name).
                aria-label={collapsed ? label : undefined}
                title={collapsed ? label : undefined}
                onClick={onNavClick}
              >
                <Icon
                  size={20}
                  className="hub-sidebar__nav-icon"
                  aria-hidden="true"
                />
                {!collapsed && (
                  <span className="hub-sidebar__nav-label">{label}</span>
                )}
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>

      {/* Collapse toggle */}
      <div className="hub-sidebar__toggle-row">
        <button
          type="button"
          className="hub-sidebar__collapse-toggle"
          onClick={onToggleCollapse}
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
        </button>
      </div>

      {/* Footer links — open in a new tab */}
      <div className="hub-sidebar__footer">
        {FOOTER_LINKS.map(({ to, label, icon: Icon }) => (
          <a
            key={to}
            href={to}
            target="_blank"
            rel="noopener noreferrer"
            className="hub-sidebar__footer-link"
            aria-label={collapsed ? label : undefined}
            title={collapsed ? label : undefined}
          >
            <Icon
              size={16}
              className="hub-sidebar__footer-icon"
              aria-hidden="true"
            />
            {!collapsed && (
              <span className="hub-sidebar__footer-label">{label}</span>
            )}
          </a>
        ))}
      </div>
    </aside>
  );
}
